import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/constants";
import LOCAL_TARIFFS from "@/tariffs";
import { useSiteSettings, waLink } from "@/siteSettings";

const MODA_DIVISOR = { Darat: 4000, Laut: 4000, Udara: 6000 };
const MODAS = Object.keys(MODA_DIVISOR);
const fmtRp = (n) => `Rp ${n.toLocaleString("id-ID")}`;

export default function FreightCalculator({ destination = "" }) {
  const settings = useSiteSettings();
  const [TARIFFS, setTariffs] = useState(LOCAL_TARIFFS);
  const [moda, setModa] = useState("Darat");
  const [dest, setDest] = useState("");

  useEffect(() => {
    axios.get(`${API}/tariffs`)
      .then((res) => { if (Array.isArray(res.data) && res.data.length > 0) setTariffs(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (destination) {
      const p = TARIFFS.find((t) => t.name === destination);
      if (p) { setDest(p.name); setModa(p.moda || "Darat"); }
    }
  }, [destination, TARIFFS]);

  const [koli, setKoli] = useState("");
  const [panjang, setPanjang] = useState("");
  const [lebar, setLebar] = useState("");
  const [tinggi, setTinggi] = useState("");
  const [berat, setBerat] = useState("");
  const [result, setResult] = useState(null);

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white";
  const labelCls = "block text-xs font-semibold text-slate-700 mb-1";

  const modaTariffs = TARIFFS.filter((t) => (t.moda || "Darat") === moda);
  const REGIONS = [...new Set(modaTariffs.map((t) => t.region))];
  const tariff = modaTariffs.find((t) => t.name === dest);
  const unitLabel = tariff?.unit === "m3" ? "m³" : "kg";

  const calculate = (e) => {
    e.preventDefault();
    const k = parseInt(koli) || 0;
    const p = parseFloat(panjang) || 0;
    const l = parseFloat(lebar) || 0;
    const t = parseFloat(tinggi) || 0;
    const b = parseFloat(berat) || 0;
    if (k <= 0 || b <= 0) return;
    const volTotal = ((p * l * t) / MODA_DIVISOR[moda]) * k;
    const chargeable = Math.max(b, volTotal);
    const kubikasi = (p * l * t * k) / 1000000;

    let estimate = null;
    if (tariff) {
      const isM3 = tariff.unit === "m3";
      const basis = isM3 ? kubikasi : chargeable;
      const billed = Math.max(basis, tariff.minKg);
      estimate = {
        billed: isM3 ? billed.toFixed(2) : Math.ceil(billed),
        price: Math.round(billed * tariff.rate),
        rate: tariff.rate,
        minKg: tariff.minKg,
        days: tariff.days,
        belowMin: basis < tariff.minKg,
        isM3,
      };
    }
    setResult({ chargeable: Math.ceil(chargeable), volTotal: volTotal.toFixed(1), kubikasi: kubikasi.toFixed(2), koli: k, berat: b, estimate });
  };

  const waText = result
    ? `Halo Nurrahma Logistics, saya minta tarif resmi pengiriman dari Makassar${dest ? ` ke ${dest}` : ""}. Moda: ${moda}. Jumlah: ${result.koli} koli. Berat aktual: ${result.berat} kg. Dimensi per koli: ${panjang || 0}x${lebar || 0}x${tinggi || 0} cm. Berat terhitung kalkulator: ${result.chargeable} kg (${result.kubikasi} m³).${result.estimate ? ` Estimasi kalkulator: ${fmtRp(result.estimate.price)}.` : ""}`
    : "";

  return (
    <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-md" data-testid="freight-calculator">
      <h3 className="text-lg font-extrabold text-slate-900 tracking-tight">Kalkulator Ongkir dari Makassar</h3>
      <p className="text-xs text-slate-600 mt-1">Hitung berat terhitung & estimasi tarif resmi kami untuk moda darat, laut, dan udara.</p>

      <form onSubmit={calculate} className="mt-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label htmlFor="calc-moda" className={labelCls}>Moda</label>
            <select id="calc-moda" value={moda} onChange={(e) => { setModa(e.target.value); setDest(""); }} className={inputCls} data-testid="calc-moda">
              {MODAS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label htmlFor="calc-dest" className={labelCls}>Kota Tujuan</label>
            <select id="calc-dest" value={dest} onChange={(e) => setDest(e.target.value)} className={inputCls} data-testid="calc-destination">
              <option value="">Pilih kota...</option>
              {REGIONS.map((r) => (
                <optgroup key={r} label={r}>
                  {modaTariffs.filter((t) => t.region === r).map((t) => (
                    <option key={t.name} value={t.name}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="calc-koli" className={labelCls}>Jumlah Koli</label>
            <input id="calc-koli" type="number" min="1" required value={koli} onChange={(e) => setKoli(e.target.value)} className={inputCls} placeholder="10" data-testid="calc-koli" />
          </div>
          <div>
            <label htmlFor="calc-berat" className={labelCls}>Berat Aktual (kg)</label>
            <input id="calc-berat" type="number" min="1" step="0.1" required value={berat} onChange={(e) => setBerat(e.target.value)} className={inputCls} placeholder="250" data-testid="calc-berat" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="calc-panjang" className={labelCls}>Panjang/koli (cm)</label>
            <input id="calc-panjang" type="number" min="0" value={panjang} onChange={(e) => setPanjang(e.target.value)} className={inputCls} placeholder="60" data-testid="calc-panjang" />
          </div>
          <div>
            <label htmlFor="calc-lebar" className={labelCls}>Lebar (cm)</label>
            <input id="calc-lebar" type="number" min="0" value={lebar} onChange={(e) => setLebar(e.target.value)} className={inputCls} placeholder="40" data-testid="calc-lebar" />
          </div>
          <div>
            <label htmlFor="calc-tinggi" className={labelCls}>Tinggi (cm)</label>
            <input id="calc-tinggi" type="number" min="0" value={tinggi} onChange={(e) => setTinggi(e.target.value)} className={inputCls} placeholder="40" data-testid="calc-tinggi" />
          </div>
        </div>
        <button type="submit" className="w-full py-3 rounded-xl bg-blue-900 text-amber-300 font-bold text-sm hover:bg-blue-950 transition-colors" data-testid="calc-submit">
          Hitung Estimasi Ongkir
        </button>
      </form>

      {result && (
        <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="calc-result">
          <p className="text-sm text-slate-800">
            Berat terhitung: <span className="font-black text-blue-950 text-lg">{result.chargeable} kg</span>
            <span className="text-xs text-slate-500"> (volumetrik {result.volTotal} kg vs aktual {result.berat} kg · volume {result.kubikasi} m³)</span>
          </p>
          {result.estimate ? (
            <div className="mt-3 pt-3 border-t border-amber-200" data-testid="calc-price">
              <p className="text-xs text-slate-600">Estimasi tarif {moda.toLowerCase()} ke <strong>{dest}</strong> ({fmtRp(result.estimate.rate)}/{unitLabel}{result.estimate.belowMin ? `, minimal ${result.estimate.minKg} ${unitLabel}` : ""}):</p>
              <p className="text-2xl font-black text-blue-950 mt-1" data-testid="calc-price-value">{fmtRp(result.estimate.price)}</p>
              <p className="text-xs text-slate-600 mt-1">Estimasi waktu: {result.estimate.days} · dihitung dari {result.estimate.billed} {unitLabel} terbilang</p>
            </div>
          ) : (
            <p className="text-xs text-slate-600 mt-2" data-testid="calc-contact-note">
              {moda !== "Darat" ? "Tarif laut/udara tujuan ini mengikuti jadwal kapal & penerbangan terbaru — tim kami hitungkan resmi." : "Kota tujuan belum ada di daftar tarif — tim kami hitungkan resmi."}
            </p>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <a href={result ? waLink(settings, waText) : waLink(settings)} target="_blank" rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors"
              data-testid="calc-wa-button">
              Minta Tarif Resmi via WhatsApp
            </a>
            <Link to="/#kontak" className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-blue-900 text-xs font-bold hover:border-blue-400 transition-colors" data-testid="calc-form-link">
              Isi Formulir Penjemputan
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
